import React, { useState, useRef, useEffect } from 'react';
import { Loader, UserSearch, X, Users } from 'lucide-react';

// Add a helper to render bold between ** for search recommendations
function renderWithBold(text) {
  // Split on ** and alternate between normal and bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

const SearchParameters = ({ parsedResume, searchRecommendations, onSearch, onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  // Removed numResults state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Hide dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Optionally filter recommendations based on input
  const filteredRecommendations = searchRecommendations && searchQuery
    ? searchRecommendations.filter(r =>
      r.replace(/\*\*([^*]+)\*\*/g, '$1').toLowerCase().includes(searchQuery.toLowerCase())
    )
    : searchRecommendations;

  // Remove handleSubmit and replace with inline handlers for two buttons
  const handleRecommendationClick = (recommendation) => {
    const cleanRecommendation = recommendation.replace(/\*\*([^*]+)\*\*/g, '$1');
    setSearchQuery(cleanRecommendation);
    setShowDropdown(false);
    setError('');
    // Optionally, focus the input after selection
    if (inputRef.current) inputRef.current.focus();
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowDropdown(false);
    setError('');
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="mb-12 flex flex-col items-center">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center bg-black/10 text-black rounded-full p-2">
            <Users className="h-6 w-6" />
          </span>
          <h3 className="text-2xl font-bold text-gray-900">People Search</h3>
        </div>
        <p className="text-gray-500 mb-6 text-sm pl-12">Find professionals by company, department, or other criteria.</p>
        <div className="backdrop-blur bg-white/10 border border-gray-200 rounded-3xl p-8 shadow-lg relative">
          <div className="mb-6 relative">
            <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-2">
              What kind of professionals are you looking for?
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <UserSearch className="h-5 w-5" />
              </span>
              <input
                id="search-query"
                type="text"
                placeholder="E.g., people from Goldman Sachs TMT"
                className={`w-full p-4 pl-12 pr-10 border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-black transition
                  ${showDropdown ? 'rounded-t-2xl rounded-b-none' : 'rounded-2xl'}
                `}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => {
                  if (searchRecommendations && searchRecommendations.length > 0) setShowDropdown(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!searchQuery.trim()) {
                      setError('Please specify search criteria');
                      return;
                    }
                    setError('');
                    setIsLoading(true);
                    onSearch(searchQuery, false); // Enter triggers normal flow
                    setIsLoading(false);
                  }
                }}
                ref={inputRef}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={handleClear}
                  tabIndex={0}
                  aria-label="Clear search"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            {/* Dropdown for recommendations */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 z-20 bg-white border border-gray-200 border-t-0 rounded-b-2xl shadow-lg max-h-60 overflow-auto animate-fade-in"
                style={{ top: '100%', marginTop: '0', width: '100%' }}
              >
                {filteredRecommendations && filteredRecommendations.length > 0 ? (
                  filteredRecommendations.map((recommendation, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleRecommendationClick(recommendation)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 text-sm text-gray-700 border-b last:border-b-0 border-gray-100 focus:bg-blue-100 transition"
                      tabIndex={0}
                    >
                      {renderWithBold(recommendation)}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-gray-400 text-sm">No recommendations found</div>
                )}
              </div>
            )}
          </div>

          {/* Helper text always below both input and dropdown */}
          <div style={{ position: 'relative', zIndex: 10 }}>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <span className="inline-block"><UserSearch className="h-4 w-4" /></span>
              Specify company, department, or other criteria
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 gap-2 flex-wrap">
            <button
              onClick={() => onBack()}
              className="text-gray-500 font-medium hover:text-gray-700 transition px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50"
            >
              Back
            </button>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  if (!searchQuery.trim()) {
                    setError('Please specify search criteria');
                    return;
                  }
                  setError('');
                  setIsLoading(true);
                  onSearch(searchQuery, true); // Go to chatbot
                  setIsLoading(false);
                }}
                className="bg-black text-white px-6 py-3 rounded-full flex items-center justify-center font-semibold text-base shadow-lg transition hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                Deep Search
              </button>
              <button
                onClick={() => {
                  if (!searchQuery.trim()) {
                    setError('Please specify search criteria');
                    return;
                  }
                  setError('');
                  setIsLoading(true);
                  onSearch(searchQuery, false); // Go directly to results
                  setIsLoading(false);
                }}
                className="bg-gray-200 text-black px-6 py-3 rounded-full flex items-center justify-center font-semibold text-base shadow-lg transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                Search Now
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Add fade-in animation for dropdown */}
      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default SearchParameters;
